import Phaser from 'phaser';
import AScene from "./AScene.js";

export default class ASpine
{
    /**
     * @param {AScene} scene 
     * @param {(TrackEntry)=>void} onComplete 
     */
    constructor(scene, x, y, key, animation, loop = false, onComplete)
    {
        this.scene = scene;
        this.spine = this.scene.add.spine(x, y, key, animation, loop);
        this.name = key;

        this.spine.state.addListener({
            complete: (trackEntry) => { 
                if(onComplete) onComplete();
                this.onComplete(trackEntry); 
            },
        });
   
        this.onCompleteCallbacks = {};
        this.setDefaultMix(.5);

        this.currentanimation = animation;

        this.spine.preUpdate = (time, delta)=>{this.preUpdate(time, delta);};
    }

    destroy()
    {
        this.spine.destroy();
        if(this.spine.mask) this.spine.mask.destroy();
        if(this.shadowSpine) this.shadowSpine.destroy();
    }

    addDropShadow(xOffset, yOffset, alpha = .4)
    {
        let track = this.spine.state.tracks[0];
        this.shadowOffset = {x: xOffset, y: yOffset};
        this.shadowSpine = this.scene.add.spine(this.spine.x + xOffset, this.spine.y + yOffset, this.name, track.animation.name, track.loop);
        
        this.shadowSpine.setSkin(null);
        
        this.shadowSpine.setSkinByName(this.spine.skeleton.skin.name);
        this.shadowSpine.skeleton.color = {r: 0, g: 0, b: 0, a: alpha};
        this.spine.setDepth(this.shadowSpine.depth + 1);
    }

    onComplete(trackEntry)
    {
        if(this.onCompleteCallbacks[trackEntry.animation.name])
        {
            this.onCompleteCallbacks[trackEntry.animation.name]();
        }
    }

    /**
    * @param {(TrackEntry, TrackEvent)=>void} onCalled 
    */
    addListener(eventName, onCalled)
    {
        this.spine.state.addListener({
            event: (track, event)=>{ if(event.data.name == eventName) onCalled(track, event); }
        });
    }

    setSkin(skinName)
    {
        if(!this.spine.skinNulled)
        {
            this.spine.setSkin(null);
            if(this.shadowSpine) this.shadowSpine.setSkin(null);
            this.spine.skinNulled = true;
        }
        
        this.spine.setSkinByName(skinName);
        if(this.shadowSpine) this.shadowSpine.setSkinByName(skinName);
    }

    setColor(hexString)
    {
        this.spine.skeleton.color.setFromString(hexString);
    }

    setAttachment(slotName, attachmentName) 
    {
        this.spine.skeleton.setAttachment(slotName, attachmentName);
    }

    /**
     * @param {number} value This sets the normalized position of the animation
     */
    setAnimationPosition(value)
    {
        let currentTrack = this.spine.state.tracks[0];
        let trackDuration = currentTrack.animationEnd;
        currentTrack.trackTime = trackDuration * value;
    }

    getAttachments()
    {
        return this.spine.skeleton.skin.attachments;
    }

    getAttachmentByName(name)
    {
        return this.spine.skeleton.getAttachmentByName(name);
    }

    play(animationName, loop = false, onComplete, timeScale = 1)
    {
        if(!this.spine.active) return;
        let animation = this.spine.play(animationName, loop);
        animation.timeScale = timeScale;
        if(this.shadowSpine) this.shadowSpine.play(animationName, loop).timeScale = timeScale;

        this.currentanimation = animationName;
        if(onComplete) this.onCompleteCallbacks[animationName] = onComplete;
        return animation;
    }

    stop()
    {
        this.spine.state.tracks[0].timeScale = 0;
        if(this.shadowSpine) this.shadowSpine.state.tracks[0].timeScale = 0;
    }

    setAnimationOverlap(animationName, loop = false, track = 0)
    {
        this.spine.state.setAnimation(track, animationName, loop);
    }

    getAnimations()
    {
        return this.spine.getAnimationList();
    }
    
    getSlots()
    {
        return this.spine.skeleton.slots;
    }

    getSlot(slotName)
    {
        return this.spine.skeleton.findSlot(slotName);
    }

    getBone(boneName)
    {
        return this.spine.findBone(boneName);
    }

    setDefaultMix(duration)
    {
        this.spine.stateData.defaultMix = duration;
    }

    setMixByName(fromName, toName, duration)
    {
        this.spine.setMix(fromName, toName, duration);
    }

    clearTracks()
    {
        this.spine.state.clearTracks();
    }

    getBounds()
    {
        return this.spine.plugin.getBounds(this.spine.skeleton);
    }

    preUpdate(time, delta)
    {
        if(this.shadowSpine != null)
        {
            this.shadowSpine.alpha = this.spine.alpha;
            this.shadowSpine.setScale(this.spine.scaleX, this.spine.scaleY);
            this.shadowSpine.angle = this.spine.angle;
            this.shadowSpine.setPosition(this.spine.x + this.shadowOffset.x, this.spine.y + this.shadowOffset.y)
        }

        let skeleton = this.spine.skeleton;
        skeleton.flipX = this.spine.flipX;
        skeleton.flipY = this.spine.flipY;

        this.spine.state.update((delta / 1000) * this.spine.timeScale);
        this.spine.state.apply(skeleton);
        this.spine.emit('spine.update', skeleton);

        if(this.hitBox)
        {
            this.hitBox.x = this.spine.x + this.hitBox.offset.x;
            this.hitBox.y = this.spine.y + this.hitBox.offset.y;
        }

        skeleton.updateWorldTransform();
    }
}